import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Edit, Globe, Archive, Trash2 } from 'lucide-react';
import { useJob } from '../hooks/useJob';
import EditJobPopup from '../components/jobs/EditJobPopup';
import JobContacts from '../components/jobs/JobContacts';
import JobTimeline from '../components/jobs/JobTimeline';
import ConfirmationPopup from '../components/ui/ConfirmationPopup';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/Tabs';
import { supabase } from '../lib/supabase';

export default function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { job, loading, error, refetch } = useJob(id!);
  const [showEdit, setShowEdit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  const handleDelete = async () => {
    try {
      const { error: deleteError } = await supabase
        .from('nrm_jobs')
        .delete()
        .eq('id', job?.id);

      if (deleteError) throw deleteError;
      navigate('/jobs');
    } catch (err) {
      console.error('Failed to delete job:', err);
    }
  };

  const handleArchive = async () => {
    try {
      const { error: updateError } = await supabase
        .from('nrm_jobs')
        .update({ 
          status: 'archived',
          updated_at: new Date().toISOString()
        })
        .eq('id', job?.id);

      if (updateError) throw updateError;
      refetch();
      setShowArchiveConfirm(false);
    } catch (err) {
      console.error('Failed to archive job:', err);
    }
  };

  if (loading) {
    return <div className="text-gray-400">Loading job details...</div>;
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  if (!job) {
    return <div className="text-gray-400">Job not found</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold">Opportunity Details</h1>
        </div>
        <div className="flex items-center gap-4">
          {job.status !== 'archived' && (
            <button
              onClick={() => setShowArchiveConfirm(true)}
              className="btn btn-secondary flex items-center gap-2"
            >
              <Archive className="w-4 h-4" />
              Archive
            </button>
          )}
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="btn btn-secondary flex items-center gap-2 text-red-500 hover:text-white hover:bg-red-500"
          >
            <Trash2 className="w-4 h-4" />
            Delete Forever
          </button>
          <button
            onClick={() => setShowEdit(true)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
          {job.job_posting_url && (
            <a
              href={job.job_posting_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary flex items-center gap-2"
            >
              <Globe className="w-4 h-4" />
              View Posting
            </a>
          )}
        </div>
      </div>

      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4">Opportunity Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-400">Position</p>
              <p className="text-lg">{job.position}</p>
            </div>
            {job.company && (
              <div>
                <p className="text-sm text-gray-400">Company</p>
                <Link 
                  to={`/companies/${job.company.id}`}
                  className="text-lg text-primary-500 hover:text-primary-400"
                >
                  {job.company.name}
                </Link>
              </div>
            )}
            {job.location && (
              <div>
                <p className="text-sm text-gray-400">Location</p>
                <p className="text-lg">{job.location}</p>
              </div>
            )}
          </div>
          <div className="space-y-4">
            {job.salary_range && (
              <div>
                <p className="text-sm text-gray-400">Salary Range</p>
                <p className="text-lg">{job.salary_range}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-400">Status</p>
              <p className="text-lg capitalize">{job.status}</p>
            </div>
          </div>
        </div>

        {(job.description || job.notes) && (
          <div className="mt-6 space-y-4">
            {job.description && (
              <div>
                <p className="text-sm text-gray-400">Description</p>
                <p className="text-lg whitespace-pre-wrap">{job.description}</p>
              </div>
            )}
            {job.notes && (
              <div>
                <p className="text-sm text-gray-400">Notes</p>
                <p className="text-lg whitespace-pre-wrap">{job.notes}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <Tabs defaultValue="contacts" className="space-y-6">
        <TabsList>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="contacts">
          <JobContacts jobId={job.id} companyId={job.company_id} />
        </TabsContent>

        <TabsContent value="timeline">
          <JobTimeline jobId={job.id} />
        </TabsContent>
      </Tabs>

      {showEdit && (
        <EditJobPopup
          job={job}
          onClose={() => setShowEdit(false)}
          onSuccess={() => {
            refetch();
            setShowEdit(false);
          }}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmationPopup
          title="Delete Opportunity"
          message="Are you sure you want to delete this opportunity? This action cannot be undone and will remove all associated data."
          confirmLabel="Delete Forever"
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {showArchiveConfirm && (
        <ConfirmationPopup
          title="Archive Opportunity"
          message="Are you sure you want to archive this opportunity? You can still view it but it will be removed from active opportunities."
          confirmLabel="Archive"
          onConfirm={handleArchive}
          onCancel={() => setShowArchiveConfirm(false)}
        />
      )}
    </div>
  );
}